import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UtilService } from '../../../../services/util.service';
import {
  API,
  ChannelDeviceStatusDTO,
  ChannelDTO,
  CoordinateClusterDTO,
  CullDTO,
  DeviceOnlineStatus,
  FrameDTO,
  PlayerCullDTO,
  ScreenScope,
} from '../../../../services/api/api';
import { SbBarChartModel, SbChartModel } from '@ngx-seatback/charts';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { ClusterDefinition, MinMaxPoints, MsOptions, SbBooleanToggleModel } from '../../../../services/global/global-interfaces';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-page-player-reporting',
  templateUrl: './player-reporting.component.html',
  styleUrls: ['./player-reporting.component.scss'],
  standalone: false,
})
export class PlayerReportingComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  togglesInitialised: boolean;
  loading: boolean;
  initialToggles: { productive: number };
  initialBooleans: SbBooleanToggleModel[];
  productive: number;
  channel: ChannelDTO;
  channelOptions: ChannelDTO[];
  barChartData: ChannelDeviceStatusDTO[];
  barChart: SbChartModel;
  radarChart: SbBarChartModel;
  timeFrameData: FrameDTO[];

  culls: CullDTO[];
  filteredPlayerCulls: PlayerCullDTO[];
  interval: number;

  onlineStatus: boolean;
  currentlyOfflineStatus: boolean;
  plannedOfflineStatus: boolean;
  offlineStatus: boolean;
  unknownStatus: boolean;

  clusterMapLoading: boolean;
  clusters: ClusterDefinition[];
  minMaxPoints: MinMaxPoints;
  debounceTimer: ReturnType<typeof setTimeout>;

  msCullOptions: MsOptions;

  _from?: SbDateTime;
  get from(): SbDateTime {
    return this._from;
  }

  set from(val) {
    this._from = val;
  }

  _till?: SbDateTime;
  get till(): SbDateTime {
    return this._till;
  }

  set till(val) {
    this._till = val;
  }

  constructor(
    public readonly util: UtilService,
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.togglesInitialised = false;
    this.loading = true;
    this.initialToggles = { productive: 1 };
    this.initialBooleans = [];
    this.productive = 1;
    this.channel = undefined;
    this.channelOptions = [];
    this.culls = [];
    this.filteredPlayerCulls = [];
    this.interval = 86400;
    this.onlineStatus = true;
    this.currentlyOfflineStatus = true;
    this.plannedOfflineStatus = true;
    this.offlineStatus = true;
    this.unknownStatus = true;
    this.clusterMapLoading = false;
    this.clusters = [];
    this.debounceTimer = null;
  }

  ngOnInit(): void {
    if (this.permissionService.hasPermission(this.permissionService.Permission.ss_nm_reporting)) {
      this.initBoolToggles();
      this.from = new SbDateTime(DateTime.local().startOf('day').minus({ months: 1 }));
      this.till = new SbDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
      this.getTimeFrameData();
    }
  }

  toggleChange(e: any) {
    this.productive = e.productive;
    if (this.togglesInitialised) {
      this.togglesInitialised = true;
    } else {
      this.requestDebounced();
    }
  }

  initBoolToggles() {
    this.onlineStatus = true;
    this.currentlyOfflineStatus = true;
    this.plannedOfflineStatus = true;
    this.offlineStatus = true;
    this.unknownStatus = true;
    this.refreshBoolToggles();
  }

  refreshBoolToggles() {
    this.initialBooleans = [];
    this.initialBooleans = [
      {
        id: 'online',
        name: _('lang.globals.online'),
        value: this.onlineStatus,
        class: 'online',
        classPreview: 'green',
      },
      {
        id: 'currentlyOffline',
        name: _('lang.globals.recOnline'),
        value: this.currentlyOfflineStatus,
        class: 'recOnline',
        classPreview: 'yellow',
      },
      {
        id: 'plannedOffline',
        name: _('lang.globals.plannedOffline'),
        value: this.plannedOfflineStatus,
        class: 'plannedOffline',
        classPreview: 'orange',
      },
      {
        id: 'offline',
        name: _('lang.globals.offline'),
        value: this.offlineStatus,
        class: 'offline',
        classPreview: 'red',
      },
      {
        id: 'unknown',
        name: _('lang.globals.unknown'),
        value: this.unknownStatus,
        class: 'unknown',
        classPreview: 'grey',
      },
    ];
  }

  boolChange(t: string, $e: boolean) {
    switch (t) {
      case 'online':
        this.onlineStatus = $e;
        break;
      case 'currentlyOffline':
        this.currentlyOfflineStatus = $e;
        break;
      case 'plannedOffline':
        this.plannedOfflineStatus = $e;
        break;
      case 'offline':
        this.offlineStatus = $e;
        break;
      case 'unknown':
        this.unknownStatus = $e;
        break;
    }
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
      this.refreshClusterMap(this.minMaxPoints);
    }, 500);
  }

  setChannelFilter(e: ChannelDTO) {
    if (!e) {
      this.channel = undefined;
    } else {
      this.channel = e;
    }
    this.reloadChartData();
  }

  setPlayerCulls(e: PlayerCullDTO[]) {
    if (!e) {
      this.filteredPlayerCulls = [];
    } else {
      this.filteredPlayerCulls = e;
    }
    this.getTimeFrameData();
  }

  getDeviceOnlineStatus(): DeviceOnlineStatus[] {
    const arr: DeviceOnlineStatus[] = [];

    if (this.onlineStatus) {
      arr.push(DeviceOnlineStatus.Online);
    }
    if (this.currentlyOfflineStatus) {
      arr.push(DeviceOnlineStatus.CurrentlyOffline);
    }
    if (this.plannedOfflineStatus) {
      arr.push(DeviceOnlineStatus.PlannedOffline);
    }
    if (this.offlineStatus) {
      arr.push(DeviceOnlineStatus.Offline);
    }
    if (this.unknownStatus) {
      arr.push(DeviceOnlineStatus.Unknown);
    }

    if (arr.length <= 0) {
      return;
    }

    return arr;
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .ss_nm_Network_channelDeviceStatus({
        productive: this.productive === 1 ? true : this.productive === 0 ? false : null,
        onlineStatus: this.getDeviceOnlineStatus(),
        playerCulls: this.util.arrayToIdArray(this.filteredPlayerCulls),
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.barChartData = data;

          this.barChartData.forEach(x => {
            if (x.deviceStatus.total === 0) {
              x.channel.name += ' (No Data)';
            }
          });

          this.reloadChartData();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  refreshClusterMap(bounds: MinMaxPoints) {
    this.clusterMapLoading = true;
    this.minMaxPoints = bounds;

    if (this.minMaxPoints) {
      // TODO ADD SUPPORT FOR GEO CULL?
      this.api
        .cs_PlayerCull_coordinateCluster({
          screenScope: ScreenScope.OWNED,
          min: this.minMaxPoints.min,
          max: this.minMaxPoints.max,
          playerCull: this.util.arrayToIdArray(this.filteredPlayerCulls),
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.clusterMapLoading = false;
          })
        )
        .subscribe({
          next: (e: CoordinateClusterDTO[]) => {
            this.clusters = e;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.clusters = [];
          },
        });
    } else {
      this.clusterMapLoading = false;
      this.clusters = [];
    }
  }

  setInterval(interval) {
    this.interval = interval;
    this.getTimeFrameData();
  }

  setTimeFrame(dates?) {
    if (dates?.fromDate && dates?.toDate) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.getTimeFrameData();
    }
  }

  getTimeFrameData() {
    this.api
      .ss_nm_Reporting_deviceStatusCountHistory({
        from: this.from,
        till: this.till,
        productive: true,
        interval: this.interval,
        playerCull: this.util.arrayToIdArray(this.filteredPlayerCulls),
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.timeFrameData = data.buckets[0].frames;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  reloadChartData() {
    this.loading = true;

    setTimeout(() => {
      let channelDataUsable = false;

      let allPlayers = 0;
      let online = 0;
      let recentlyOnline = 0;
      let plannedOffline = 0;
      let offline = 0;
      let noSim = 0;
      let unknown = 0;
      let external = 0;
      let proposed = 0;
      let tempDisabled = 0;
      let service = 0;
      let paired = 0;

      const channelOptionsTmp: ChannelDTO[] = [];

      for (const i of this.barChartData) {
        channelOptionsTmp.push(i.channel);

        if (this.channel) {
          channelDataUsable = this.channel.id === i.channel.id;
        } else {
          channelDataUsable = true;
          this.channelOptions = channelOptionsTmp;
        }

        if (channelDataUsable) {
          allPlayers += i.deviceStatus.total;
          online += i.deviceStatus.quotaOnlineStatus_Online;
          recentlyOnline += i.deviceStatus.quotaOnlineStatus_CurrentlyOffline;
          plannedOffline += i.deviceStatus.quotaOnlineStatus_PlannedOffline;
          offline += i.deviceStatus.quotaOnlineStatus_Offline;
          unknown += i.deviceStatus.quotaOnlineStatus_Unknown;
          noSim += i.deviceStatus.subsetOffline;
          tempDisabled += i.deviceStatus.subsetDisabled;
          proposed += i.deviceStatus.subsetProposed;
          external += i.deviceStatus.subsetExternal;
          service += i.deviceStatus.subsetServiceRequested;
          paired += i.deviceStatus.subsetPaired;
        }
      }

      this.barChart = {
        title: 'Player Overview',
        height: 240,
        width: 100,
        valueAssignments: [
          {
            value: 'value',
            categories: ['category'],
            valueName: 'Count',
          },
        ],
        legend: false,
        labelBullet: true,
        data: this.getBarChartData(allPlayers, online, recentlyOnline, plannedOffline, offline, unknown),
      };
      this.barChart.loadingDone = true;
      this.radarChart = {
        title: 'Player Overview',
        height: 240,
        width: 100,
        valueAssignments: [
          {
            value: 'value',
            categories: ['category'],
            valueName: 'Count',
          },
        ],
        labelBullet: true,
        data: [
          {
            category: this.translate.instant('lang.globals.noSim'),
            value: noSim,
            color: '#ff7600',
          },
          {
            category: this.translate.instant('lang.globals.tempDisabled'),
            value: tempDisabled,
            color: '#ff7600',
          },
          {
            category: this.translate.instant('lang.globals.external'),
            value: external,
            color: '#928990',
          },
          {
            category: this.translate.instant('lang.globals.proposedInstallation'),
            value: proposed,
            color: '#928990',
          },
          {
            category: this.translate.instant('lang.globals.service'),
            value: service,
            color: '#FF1414',
          },
          {
            category: this.translate.instant('lang.globals.paired'),
            value: paired,
            color: '#016AA2',
          },
        ],
      };
      this.radarChart.loadingDone = true;
      this.loading = false;
    });
  }

  getBarChartData(allPlayers, online, recentlyOnline, plannedOffline, offline, unknown) {
    const arr = [];
    arr.push({
      category: this.translate.instant('lang.globals.total'),
      value: allPlayers,
      color: '#016AA2',
    });
    if (this.onlineStatus) {
      arr.push({
        category: this.translate.instant('lang.globals.online'),
        value: online,
        color: '#2CC361',
      });
    }
    if (this.currentlyOfflineStatus) {
      arr.push({
        category: this.translate.instant('lang.globals.recOnline'),
        value: recentlyOnline,
        color: '#FFC800',
      });
    }
    if (this.plannedOfflineStatus) {
      arr.push({
        category: this.translate.instant('lang.globals.plannedOffline'),
        value: plannedOffline,
        color: '#ff7600',
      });
    }
    if (this.offlineStatus) {
      arr.push({
        category: this.translate.instant('lang.globals.offline'),
        value: offline,
        color: '#FF1414',
      });
    }
    if (this.unknownStatus) {
      arr.push({
        category: this.translate.instant('lang.globals.unknown'),
        value: unknown,
        color: '#999dae',
      });
    }
    return arr;
  }

  update($event, type: string) {
    if (type === 'PLAYER_CULLS') {
      if ($event && $event?.length > 0) {
        this.filteredPlayerCulls = $event;
      } else {
        this.filteredPlayerCulls = [];
      }
    }
    this.requestDebounced();
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'PLAYER_CULLS') {
      if (options) {
        this.msCullOptions = options;
      }
      if (this.msCullOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.msCullOptions.search),
              type: 'PlayerCull',
            },
            this.msCullOptions.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (culls: CullDTO[]) => {
              this.culls = culls;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }
}
